/**
 * Offline read-cache (snapshot) for the asset list — inventory mode.
 *
 * The write path already survives offline via useOptimisticQueue (IndexedDB
 * save queue + auto-flush on reconnect). This module adds the READ path: a
 * per-activity snapshot of the *list projection* (NO photos, NO full
 * document_checklist — verified server-side by GET /assets/offline-snapshot
 * which projects with the same LIST_PROJECTION as GET /assets) so the
 * dashboard list still loads with zero connectivity.
 *
 * Security policy:
 * - Snapshot is scoped by userId + activityId (meta store); a login by a
 *   DIFFERENT user must call ensureSnapshotOwner() → clearAllSnapshots().
 * - TTL 7 days: an older snapshot is never served (treated as absent).
 * - Manual logout clears everything (clearAllSnapshots from App.js);
 *   auto-401/idle logout does NOT — field data protection.
 */
import { openDB } from "idb";
import { ASSET_SCALAR_FIELDS } from "./assetFields";
import axios from "axios";
import { isQuotaExceeded } from "./idbErrors";
import { TENGGAT_BAKA, muatAndal } from "./muatAndal";
import { gabungVersiBaris } from "./rekonsiliasiBaris";
import { buatCacheQuerySnapshot, queryHalamanSnapshot } from "./cacheQuerySnapshot";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DB_NAME = "aman_offline_snapshot";
// v2: proyeksi list menambah field berlebih/sengketa — snapshot lama tidak
// memilikinya dan sync delta tak akan mengisi ulang baris yang tak berubah,
// jadi upgrade mengosongkan cache agar sync berikutnya full resync.
const DB_VERSION = 2;
// Muat ulang proyeksi saat daring tanpa menghapus cache baca/antrean luring.
// v3 memuat SIMAN/PSP yang telah diverifikasi satkernya. Sanitasi proyeksi
// tidak mengubah version/updated_at aset, sehingga delta tidak memperbaiki
// badge lama. Tarik penuh sekali saat daring; cache dan antrean tetap ada.
const VERSI_PROYEKSI = 3;
const ASSET_STORE = "assets"; // keyed by asset id, indexed by activity_id
const META_STORE = "meta";    // keyed by activityId → {activityId, userId, lastSync, count}

const queryCache = buatCacheQuerySnapshot();
let revisionChannel;
function invalidateQueryCache(activityId) {
  queryCache.clear();
  try { revisionChannel?.postMessage({ activityId }); } catch { /* cache tetap lokal */ }
}
function listenForRevisions() {
  if (revisionChannel || typeof window === "undefined" || !window.BroadcastChannel) return;
  revisionChannel = new window.BroadcastChannel("aman-snapshot-revision");
  revisionChannel.onmessage = () => queryCache.clear();
  // Tab frontend lama tidak menaikkan revision ketika upsert; kembali ke tab
  // ini tetap membaca ulang index sebelum memakai hasil cache sebelumnya.
  window.addEventListener("focus", () => queryCache.clear());
  revisionChannel.unref?.();
}
async function bumpRevision(db, activityId) {
  invalidateQueryCache(activityId);
  try {
    const tx = db.transaction(META_STORE, "readwrite");
    const meta = await tx.store.get(activityId);
    if (meta) await tx.store.put({ ...meta, revision: (Number(meta.revision) || 0) + 1 });
    await tx.done;
  } catch (e) { if (!isQuotaExceeded(e)) throw e; }
}

const PAGE_LIMIT = 1000;
// Refuse to serve a snapshot older than this — stale field data is worse
// than an explicit "reconnect to resync" message.
export const SNAPSHOT_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 hari

// Fields we allow into the snapshot store — mirrors the backend
// LIST_PROJECTION. Anything else (full photos, document_checklist, photo
// blobs from optimistic payloads) is stripped before writing.
const SNAPSHOT_FIELDS = [
  ...ASSET_SCALAR_FIELDS,
  "id", "bast_file_id", "bast_filename", "bast_terakhir", "amanah_bast",
  "thumbnail", "thumbnail_index", "gallery_thumbnail", "created_at", "updated_at",
  "activity_id", "version", "stiker_photo_index", "di_denah", "denah_nama",
  "denah_jalur", "denah_titik", "photo_count", "doc_total", "doc_checked",
  "doc_summary", "siman", "psp",
];

let databasePromise;
function getDB() {
  if (databasePromise) return databasePromise;
  databasePromise = openDB(DB_NAME, DB_VERSION, {
    upgrade(db, oldVersion, _newVersion, tx) {
      if (!db.objectStoreNames.contains(ASSET_STORE)) {
        const store = db.createObjectStore(ASSET_STORE, { keyPath: "id" });
        store.createIndex("by-activity", "activity_id", { unique: false });
      }
      if (!db.objectStoreNames.contains(META_STORE)) {
        db.createObjectStore(META_STORE, { keyPath: "activityId" });
      }
      if (oldVersion > 0 && oldVersion < 2) {
        tx.objectStore(ASSET_STORE).clear();
        tx.objectStore(META_STORE).clear();
      }
    },
    blocking() { databasePromise?.then(db => db.close()); databasePromise = null; },
    terminated() { databasePromise = null; },
  }).catch(error => { databasePromise = null; throw error; });
  return databasePromise;
}

// Ask the browser to protect our IndexedDB from storage-pressure eviction.
// Request at most once per session — repeated calls can re-prompt on some
// browsers.
let persistRequested = false;
async function requestPersistentStorage() {
  if (persistRequested) return;
  persistRequested = true;
  try {
    if (navigator.storage?.persist) await navigator.storage.persist();
  } catch {
    // Best-effort — snapshot still works, just evictable under pressure.
  }
}

// Keep only list-projection fields (never photos / full checklist).
function toSnapshotRow(row) {
  const clean = {};
  for (const f of SNAPSHOT_FIELDS) {
    if (row[f] !== undefined) clean[f] = row[f];
  }
  return clean;
}

function isExpired(meta) {
  // Kesegaran dibaca dari `disegarkanPada` — stempel "kapan terakhir kali kita
  // benar-benar menarik data", yang SELALU ditulis. Ia sengaja DIPISAH dari
  // `lastSync`, yang kini murni kursor delta dan berhenti maju saat kuota
  // penuh (lihat _syncSnapshot). Tanpa pemisahan ini, sinkron parsial di
  // perangkat penuh akan membuat cache-nya langsung dianggap kedaluwarsa —
  // persis cache sebagian yang tadi susah payah kita pertahankan.
  // Rekaman lama (sebelum PR ini) tak punya stempelnya → jatuh ke lastSync.
  const stempel = meta?.disegarkanPada || meta?.lastSync;
  if (!stempel) return true;
  const age = Date.now() - new Date(stempel).getTime();
  return !Number.isFinite(age) || age > SNAPSHOT_TTL_MS;
}

/** Raw meta record for an activity ({activityId, userId, lastSync, count}) or null. */
export async function snapshotMeta(activityId) {
  if (!activityId) return null;
  try {
    return (await (await getDB()).get(META_STORE, activityId)) || null;
  } catch {
    return null;
  }
}

/** True when a snapshot exists but is past its 7-day TTL. */
export function isSnapshotExpired(meta) {
  return !!meta && isExpired(meta);
}

/**
 * Sync the snapshot for one activity. Delta when possible (since=lastSync),
 * full otherwise. onProgress({loaded, total, pct}) fires per page.
 * Returns {count, lastSync}.
 */
/**
 * Satu sinkron per kegiatan pada satu waktu.
 *
 * `syncSnapshot` tak bisa dibatalkan: cleanup effect pemanggil hanya menyetel
 * penanda `cancelled`, sedangkan permintaan dan tulisan IndexedDB-nya jalan
 * terus. Dengan coba-ulang per halaman, satu jalankan hidup jauh lebih lama —
 * cukup lama untuk bertumpang tindih dengan jalankan berikutnya ketika petugas
 * menyimpulkan macet lalu mematikan-menyalakan mode inventarisasi.
 *
 * Dua jalankan FULL yang tumpang tindih saling merusak: yang selesai belakangan
 * menghitung `stale = existing − fetchedIds` miliknya sendiri, sehingga baris
 * yang baru ditulis jalankan lain (atau oleh `upsertSnapshotAsset`) dianggap
 * usang dan DIHAPUS dari cache luring — aset yang sah lenyap sebelum petugas
 * berangkat ke lapangan. Pemanggil kedua kini menunggu hasil jalankan pertama
 * alih-alih memulai jalankan tandingan.
 */
const _sinkronBerjalan = new Map();   // activityId -> Promise

export function syncSnapshot(activityId, userId, onProgress, opsi = {}) {
  const berjalan = _sinkronBerjalan.get(activityId);
  if (berjalan) return berjalan;
  const janji = _syncSnapshot(activityId, userId, onProgress, opsi)
    .finally(() => { _sinkronBerjalan.delete(activityId); });
  _sinkronBerjalan.set(activityId, janji);
  return janji;
}

async function _syncSnapshot(activityId, userId, onProgress, { forceFull = false } = {}) {
  if (!activityId || !userId) throw new Error("activityId dan userId wajib diisi");
  await requestPersistentStorage();

  const db = await getDB();
  const meta = await db.get(META_STORE, activityId);
  // Delta only when the snapshot belongs to this user and is still fresh —
  // otherwise resync from scratch (also the different-user defense in depth;
  // the primary guard is ensureSnapshotOwner on login).
  const canDelta = !forceFull && !!meta && meta.userId === userId && !isExpired(meta)
    && meta.versiProyeksi === VERSI_PROYEKSI;
  const since = canDelta ? meta.lastSync : "";
  const fullSync = !since;

  let lastSyncCursor = null; // server_time of the FIRST page = next delta cursor
  const fetchedIds = fullSync ? new Set() : null;
  let cursor = ""; // keyset: id item terakhir halaman sebelumnya (ganti offset skip)
  let total = 0;
  let loaded = 0;
  // Kuota IndexedDB terlampaui di tengah sync (perangkat nyaris penuh): berhenti
  // menulis dengan anggun, layani cache sebagian yang sudah ada — jangan crash.
  let quotaHit = false;

  for (;;) {
    const params = new URLSearchParams({ activity_id: activityId, limit: String(PAGE_LIMIT) });
    if (cursor) params.append("cursor", cursor);
    if (since) params.append("since", since);
    // Sinkron ini berhalaman-halaman: satu kegiatan besar bisa puluhan
    // permintaan berturut-turut, dan justru dilakukan tepat sebelum berangkat
    // ke lapangan — sering di sinyal yang paling buruk. Dulu SATU halaman yang
    // gagal membatalkan seluruh sinkron dan operator berangkat tanpa cache.
    // Kini tiap halaman diulang otomatis untuk kegagalan yang memang layak
    // diulang (jaringan/tenggat/server), dengan jeda menanjak. Aman diulang:
    // ini GET, dan kursor keyset-nya tak bergerak sebelum halaman berhasil.
    // TENGGAT_BAKA, bukan TENGGAT_BERAT: dengan 3 percobaan, 60 dtk per
    // percobaan berarti satu halaman bisa menahan sinkron ~3 menit dan bar
    // progresnya BEKU selama itu — petugas menyimpulkan macet lalu memulai
    // sinkron kedua. 20 dtk menahan paling lama ~63 dtk per halaman.
    const r = await muatAndal(() => axios.get(
      `${API}/assets/offline-snapshot?${params.toString()}`,
      { timeout: TENGGAT_BAKA }));
    const { items = [], deleted_ids: deletedIds = [], requires_full_refresh: needsFull,
            next_cursor: nextCursor = "" } = r.data || {};
    total = r.data?.total ?? total;
    if (lastSyncCursor === null) lastSyncCursor = r.data?.server_time || new Date().toISOString();

    // A bulk delete happened since our cursor — tombstones don't carry ids
    // for it, so restart as a full sync (reconciles all deletes).
    if (needsFull && !fullSync) {
      // Lewat fungsi DALAM, bukan pembungkus ber-penjaga: jalankan ini masih
      // memegang slotnya sendiri, jadi memanggil pembungkus akan mengembalikan
      // promise-nya sendiri dan menggantung selamanya.
      return _syncSnapshot(activityId, userId, onProgress, { forceFull: true });
    }

    // Apply tombstones (first delta page only carries them)
    if (deletedIds.length) {
      const tx = db.transaction(ASSET_STORE, "readwrite");
      for (const id of deletedIds) tx.store.delete(id);
      await tx.done;
      await bumpRevision(db, activityId);
    }

    if (items.length) {
      const staged = [];
      for (const item of items) {
        const row = toSnapshotRow(item);
        if (row.id && row.activity_id === activityId) staged.push(row);
      }
      try {
        const tx = db.transaction(ASSET_STORE, "readwrite");
        for (const row of staged) {
          const lama = await tx.store.get(row.id);
          await tx.store.put(gabungVersiBaris(lama, row));
        }
        await tx.done;
        await bumpRevision(db, activityId);
        for (const row of staged) fetchedIds?.add(row.id);
        loaded += items.length;
      } catch (e) {
        // Kuota penuh → hentikan sync dengan anggun (cache sebalumnya tetap
        // dilayani). Error lain (mis. store hilang) dilempar ke pemanggil.
        if (!isQuotaExceeded(e)) throw e;
        quotaHit = true;
        break;
      }
    }

    onProgress?.({ loaded, total, pct: total > 0 ? Math.min(100, Math.round((loaded / total) * 100)) : 100 });

    if (items.length < PAGE_LIMIT) break;
    // Keyset: lanjut dari id item terakhir (server kirim next_cursor). Bila
    // server tak beri kursor lagi, berhenti — jaga dari loop tak berujung.
    cursor = nextCursor;
    if (!cursor) break;
  }

  // Full sync: reconcile deletes — drop rows of this activity the server no
  // longer returned (no tombstone log needed for this path). DILEWATI saat
  // quotaHit: kita berhenti lebih awal, jadi banyak id sah belum masuk
  // fetchedIds — mereka bukan "stale", menghapusnya justru mengecilkan cache.
  if (fullSync && fetchedIds && !quotaHit) {
    const existing = await db.getAllKeysFromIndex(ASSET_STORE, "by-activity", activityId);
    const stale = existing.filter((id) => !fetchedIds.has(id));
    if (stale.length) {
      const tx = db.transaction(ASSET_STORE, "readwrite");
      for (const id of stale) tx.store.delete(id);
      await tx.done;
      await bumpRevision(db, activityId);
    }
  }

  const count = (await db.getAllKeysFromIndex(ASSET_STORE, "by-activity", activityId)).length;
  // KURSOR DELTA hanya boleh maju bila SELURUH halaman berhasil ditulis.
  //
  // `lastSyncCursor` diisi dari `server_time` halaman PERTAMA. Bila kuota
  // perangkat penuh di halaman ke-7, loop berhenti (`quotaHit`) — tetapi
  // menuliskan kursor itu tetap berarti "kami sudah menarik semuanya sampai
  // detik ini". Sinkron berikutnya berangkat sebagai delta `since=kursor`,
  // sehingga baris yang belum sempat ditarik punya `updated_at < since` dan
  // TIDAK PERNAH ikut lagi. Lubangnya permanen sampai TTL 7 hari habis —
  // dan toast "kosongkan ruang lalu sinkron ulang" menyuruh pengguna
  // melakukan hal yang justru tidak menyembuhkannya.
  //
  // Karena itu saat quotaHit kursornya DIPERTAHANKAN apa adanya. Bila belum
  // pernah ada kursor (sinkron penuh pertama yang kandas), ia tetap kosong →
  // sinkron berikutnya otomatis full, bukan delta.
  const newMeta = {
    activityId, userId, count,
    versiProyeksi: quotaHit ? meta?.versiProyeksi : VERSI_PROYEKSI,
    lastSync: quotaHit ? (meta?.lastSync || "") : lastSyncCursor,
    disegarkanPada: new Date().toISOString(),
  };
  let metaTersimpan = newMeta;
  try {
    const tx = db.transaction(META_STORE, "readwrite");
    const latestMeta = await tx.store.get(activityId);
    newMeta.revision = (Number(latestMeta?.revision) || 0) + 1;
    await tx.store.put(newMeta);
    await tx.done;
    invalidateQueryCache(activityId);
  } catch (e) {
    // Meta ~beberapa byte; bila ini pun kena kuota, catat tanpa crash. Snapshot
    // parsial tanpa meta baru akan dilayani berdasarkan meta lama (delta) atau
    // dianggap absen (full) — tetap konsisten, tak rusak.
    if (!isQuotaExceeded(e)) throw e;
    quotaHit = true;
    metaTersimpan = meta || null; // yang benar-benar ada di disk: rekaman LAMA
  }
  // Kembalikan kursor yang BENAR-BENAR tersimpan, bukan yang sempat dihitung —
  // bar "Tersinkron pukul …" tidak boleh mengklaim kesegaran yang tak ada.
  return { count, lastSync: metaTersimpan?.lastSync || "", partial: quotaHit };
}

/** Paged dashboard reader: cache IDs/statistics, fetch media only for the page. */
export async function getSnapshotPage(activityId, { ownerId, kodeSatker = "", search = "", category = [],
  filters = {}, sort = "newest", page = 1, size = 50, pendingRevision = "", signal } = {}) {
  if (!activityId) return null;
  listenForRevisions();
  const db = await getDB();
  for (let attempt = 0; attempt < 2; attempt++) {
    const meta = await db.get(META_STORE, activityId);
    if (!meta || isExpired(meta) || (ownerId && meta.userId !== ownerId)) return null;
    const source = {
      keys: () => db.getAllKeysFromIndex(ASSET_STORE, "by-activity", activityId),
      rows: async ids => {
        if (!ids.length) return [];
        const tx = db.transaction(ASSET_STORE, "readonly");
        const rows = await Promise.all(ids.map(id => tx.store.get(id)));
        await tx.done;
        return rows.filter(row => row?.activity_id === activityId);
      },
    };
    const result = await queryHalamanSnapshot({ source, cache: queryCache,
      scope: [ownerId || meta.userId, kodeSatker, activityId], meta,
      query: { search, category, filters, sort }, page, size, pendingRevision, signal });
    const latestMeta = await db.get(META_STORE, activityId);
    if (latestMeta?.userId === meta.userId && latestMeta?.revision === meta.revision &&
      latestMeta?.lastSync === meta.lastSync && !isExpired(latestMeta)) return { ...result, meta: latestMeta };
    queryCache.clear();
  }
  throw new Error("Data offline sedang diperbarui. Coba muat ulang daftar setelah sinkron selesai.");
}

/**
 * Legacy/full-map reader: all snapshot rows for an activity, sorted created_at desc.
 * Returns null when no servable snapshot exists (absent OR past the 7-day
 * TTL — check isSnapshotExpired(await snapshotMeta(id)) to tell them apart).
 */
export async function getSnapshotAssets(activityId) {
  if (!activityId) return null;
  try {
    const db = await getDB();
    const meta = await db.get(META_STORE, activityId);
    if (!meta || isExpired(meta)) return null; // TTL: expired = absent
    const rows = await db.getAllFromIndex(ASSET_STORE, "by-activity", activityId);
    rows.sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")));
    return rows;
  } catch {
    return null;
  }
}

/**
 * Upsert one (optimistically edited) row so an offline reload shows the
 * change. No-ops unless a snapshot already exists for the activity, and
 * always strips non-projection fields (photos etc.) before writing.
 */
export async function upsertSnapshotAsset(activityId, row) {
  if (!activityId || !row?.id) return;
  try {
    const db = await getDB();
    const meta = await db.get(META_STORE, activityId);
    if (!meta) return; // no snapshot for this activity — nothing to keep fresh
    // BACA-GABUNG-TULIS, bukan ganti-buta (temuan audit G3 — tiga cacat
    // berakar di sini): (1) pemanggil sering hanya membawa POTONGAN baris
    // (PATCH edit luring; baris tak ketemu di daftar state karena galeri &
    // kartu HP memakai daftar berbeda) → ganti-buta menjadikan potongan itu
    // seluruh baris, field lain lenyap dari data luring; (2) respons tulis
    // server tak memuat field turunan proyeksi list (doc_total/doc_checked/
    // doc_summary/siman) → ganti-buta menghapus nilainya yang sudah benar.
    // Menggabung dengan rekaman lama membuat potongan tetap potongan.
    // Satu transaksi baca-tulis: dua upsert paralel tidak boleh membaca dasar
    // yang sama lalu menimpa hasil satu sama lain (termasuk versi lebih baru).
    const tx = db.transaction(ASSET_STORE, "readwrite");
    const lama = await tx.store.get(row.id);
    const dasar = lama && lama.activity_id === activityId ? lama : {};
    const clean = toSnapshotRow({ ...gabungVersiBaris(dasar, row), activity_id: activityId });
    await tx.store.put(clean);
    await tx.done;
    await bumpRevision(db, activityId);
  } catch {
    // Best-effort cache maintenance — the queue still holds the real change.
  }
}

/** Hapus SATU baris dari snapshot (setelah aset dihapus) agar reload offline
 *  tidak menampilkan aset yang sudah tidak ada. */
export async function removeSnapshotAsset(activityId, id) {
  if (!activityId || !id) return;
  try {
    const db = await getDB();
    const meta = await db.get(META_STORE, activityId);
    if (!meta) return;
    await db.delete(ASSET_STORE, id);
    await bumpRevision(db, activityId);
  } catch {
    // best-effort — TTL akan membereskan bila gagal
  }
}

/** Remove one activity's snapshot (rows + meta). */
export async function clearSnapshot(activityId) {
  if (!activityId) return;
  invalidateQueryCache(activityId);
  try {
    const db = await getDB();
    const keys = await db.getAllKeysFromIndex(ASSET_STORE, "by-activity", activityId);
    const tx = db.transaction([ASSET_STORE, META_STORE], "readwrite");
    for (const id of keys) tx.objectStore(ASSET_STORE).delete(id);
    tx.objectStore(META_STORE).delete(activityId);
    await tx.done;
  } catch {
    // ignore — worst case the TTL retires it
  }
}

/** Wipe every snapshot (all activities). Used on manual logout / user switch. */
export async function clearAllSnapshots() {
  invalidateQueryCache();
  try {
    const db = await getDB();
    const tx = db.transaction([ASSET_STORE, META_STORE], "readwrite");
    tx.objectStore(ASSET_STORE).clear();
    tx.objectStore(META_STORE).clear();
    await tx.done;
  } catch {
    // ignore
  }
}

/**
 * Login guard: if any stored snapshot belongs to a DIFFERENT user, wipe all
 * snapshots first — cached asset data must never leak across accounts on a
 * shared device. Same-user re-login keeps the cache (field data protection).
 */
export async function ensureSnapshotOwner(userId) {
  if (!userId) return;
  try {
    const db = await getDB();
    const metas = await db.getAll(META_STORE);
    if (metas.some((m) => m?.userId && m.userId !== userId)) {
      await clearAllSnapshots();
    }
  } catch {
    // ignore
  }
}
